// PetCard.jsx
import React from "react";
import { Box, Typography, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { PiDog, PiCat } from "react-icons/pi";
import {
  FaTag,
  FaHeartbeat,
  FaSyringe,
  FaUtensils,
  FaCheck,
} from "react-icons/fa";
import { useProfile } from "../../context/ProfileContext";

const COLOR_HEX_MAP = {
  white: "#FFFFFF",
  black: "#1F2937",
  brown: "#795548",
  golden: "#F4B400",
  gold: "#F4B400",
  grey: "#808080",
  gray: "#808080",
  orange: "#F59E0B",
  cream: "#FFF5DC",
  tan: "#D2B48C",
  silver: "#C0C0C0",
  fawn: "#E5AA70",
  red: "#EF4444",
  yellow: "#EAB308",
  blue: "#3B82F6",
  chocolate: "#7B3F00",
  beige: "#F5F5DC",
  apricot: "#FBCEB1",
  charcoal: "#36454F",
  sable: "#8D7B68",
};

const getColorHex = (colorVal) => {
  if (!colorVal) return "#d1d5db";
  if (typeof colorVal === "object") {
    if (colorVal.hex) return colorVal.hex;
    if (colorVal.code) return colorVal.code;
    if (colorVal.color) return colorVal.color;
    colorVal = colorVal.name || colorVal.label || "";
  }
  const s = String(colorVal).trim();
  if (s.startsWith("#") || s.startsWith("rgb")) return s;
  return COLOR_HEX_MAP[s.toLowerCase()] || "#F4B400";
};

const PetCard = ({
  pet,
  onEdit = () => {},
  vaccinationLoading = false,
  serviceName,
}) => {
  const { profileData } = useProfile();
  const sizeCoatSelection = profileData?.sizeCoatSelection || "size_and_coat";
  const isSizeRequired = sizeCoatSelection !== "only_coat";
  const isCoatRequired = sizeCoatSelection !== "only_size";

  const targetPet = Array.isArray(pet) ? pet[0] : pet;
  if (
    !targetPet ||
    typeof targetPet !== "object" ||
    Object.keys(targetPet).length === 0
  ) {
    return null;
  }

  const asText = (value) => {
    if (value == null) return "";
    if (typeof value === "string" || typeof value === "number")
      return String(value);
    if (typeof value === "object") {
      return (
        value.breed_name ||
        value.pet_type_name ||
        value.size_label ||
        value.name ||
        value.label ||
        ""
      ).toString();
    }
    return "";
  };

  // Global vaccination requirement from vendor profile
  const vaccineSetting =
    profileData?.systemSettings?.vaccination_requirement ||
    profileData?.vaccination_requirement;
  const globalEnabled = vaccineSetting?.enabled === true;
  const vaccinationEnabled = globalEnabled;

  // Color
  const colors = (() => {
    const raw =
      targetPet?.color ||
      targetPet?.colors ||
      targetPet?.pet_color ||
      targetPet?.custom_fields?.["pet.color"] ||
      [];
    if (Array.isArray(raw)) {
      return raw
        .map((c) =>
          typeof c === "string" ? c.trim() : c?.name || c?.label || c?.color || "",
        )
        .filter(Boolean);
    }
    if (typeof raw === "string" && raw.trim()) {
      return raw
        .split(/[,;|]+/)
        .map((c) => c.trim())
        .filter(Boolean);
    }
    return [];
  })();

  // Build basic info line
  const infoFields = [];
  const petTypeName = asText(
    targetPet?.pet_type?.name || targetPet?.pet_type || targetPet?.type,
  );
  if (petTypeName) infoFields.push(petTypeName);
  if (asText(targetPet?.breed)) infoFields.push(asText(targetPet.breed));
  if (asText(targetPet?.size)) infoFields.push(asText(targetPet.size));
  if (targetPet?.age) {
    infoFields.push(
      String(targetPet.age).includes("yr")
        ? targetPet.age
        : `${targetPet.age} yrs`,
    );
  }
  if (targetPet?.gender && targetPet.gender !== "Select Gender") {
    infoFields.push(
      targetPet.gender.charAt(0).toUpperCase() +
        targetPet.gender.slice(1).toLowerCase(),
    );
  }
  if (targetPet?.weight_kg || targetPet?.weight) {
    infoFields.push(`${targetPet.weight_kg || targetPet.weight} kg`);
  }

  // Tags
  const tags = (() => {
    const raw =
      targetPet?.tags ||
      targetPet?.pet_tags ||
      targetPet?.custom_fields?.["pet.tags"] ||
      [];
    if (Array.isArray(raw)) {
      return raw
        .map((t) =>
          typeof t === "string" ? t.trim() : t?.name || t?.label || "",
        )
        .filter(Boolean);
    }
    if (typeof raw === "string" && raw.trim()) {
      return raw
        .split(/[,;|]+/)
        .map((t) => t.trim())
        .filter(Boolean);
    }
    return [];
  })();

  // Medical Conditions
  const medicalConditions = (() => {
    const raw =
      targetPet?.medical_conditions ||
      targetPet?.medicalConditions ||
      targetPet?.medical_condition ||
      targetPet?.custom_fields?.["pet.medical_condition"] ||
      [];
    if (Array.isArray(raw)) {
      return raw
        .map((m) => {
          if (typeof m === "string") return m.trim();
          if (m && typeof m === "object") {
            return (
              m.name || m.condition || m.label || m.medical_condition || ""
            );
          }
          return "";
        })
        .filter(Boolean);
    }
    if (typeof raw === "string" && raw.trim()) {
      return raw
        .split(/[,;|]+/)
        .map((m) => m.trim())
        .filter(Boolean);
    }
    return [];
  })();

  // Diet (Meal Type)
  const dietList = (() => {
    const raw =
      targetPet?.meal_type ||
      targetPet?.mealType ||
      targetPet?.diet ||
      targetPet?.what_to_feed ||
      targetPet?.custom_fields?.["pet.meal_type"] ||
      [];
    let list = [];
    if (Array.isArray(raw)) {
      list = raw
        .map((d) =>
          typeof d === "string"
            ? d.trim()
            : d?.name || d?.label || d?.meal_type || "",
        )
        .filter(Boolean);
    } else if (typeof raw === "string" && raw.trim()) {
      list = raw
        .split(/[,;|]+/)
        .map((d) => d.trim())
        .filter(Boolean);
    }
    if (
      list.length === 0 &&
      Array.isArray(targetPet?.meals) &&
      targetPet.meals.length > 0
    ) {
      const mealNames = targetPet.meals
        .map((m) => m?.meal_type || m?.type || m?.name || "")
        .filter(Boolean);
      if (mealNames.length > 0) list = mealNames;
    }
    return [...new Set(list)];
  })();

  // Vaccinations list
  const vaccineList = (() => {
    const list = [];
    const recs = targetPet?.vaccination_records || [];
    if (Array.isArray(recs)) {
      recs.forEach((r) => {
        const name = r?.vaccine || r?.vaccination || r?.name || "";
        if (
          name &&
          !list.some((item) => item.toLowerCase() === name.toLowerCase())
        ) {
          list.push(name);
        }
      });
    }
    const vaxs = targetPet?.vaccinations || [];
    if (Array.isArray(vaxs)) {
      vaxs.forEach((v) => {
        const name =
          typeof v === "string" ? v.trim() : v?.name || v?.vaccine || "";
        if (
          name &&
          !list.some((item) => item.toLowerCase() === name.toLowerCase())
        ) {
          list.push(name);
        }
      });
    } else if (typeof vaxs === "string" && vaxs.trim()) {
      vaxs.split(/[,;|]+/).forEach((v) => {
        const trimmed = v.trim();
        if (
          trimmed &&
          !list.some((item) => item.toLowerCase() === trimmed.toLowerCase())
        ) {
          list.push(trimmed);
        }
      });
    }
    return list;
  })();

  // Rabies check
  const hasRabiesVaccination = () => {
    const vaxList = targetPet?.vaccinations || [];
    const recList = targetPet?.vaccination_records || [];
    const statusText = (
      targetPet?.vaccination_status ||
      targetPet?.vaccinationStatus ||
      ""
    ).toLowerCase();
    const rabiesRegex = /rabies/i;
    if (
      vaxList.some((v) =>
        rabiesRegex.test(
          typeof v === "string" ? v : v?.name || v?.vaccine || "",
        ),
      )
    )
      return true;
    if (recList.some((r) => rabiesRegex.test(r?.vaccine || r?.name || "")))
      return true;
    if (rabiesRegex.test(statusText)) return true;
    return false;
  };

  const hasVaccination = vaccinationEnabled && hasRabiesVaccination();
  const showVaccinationLoading =
    vaccinationEnabled && vaccinationLoading && !hasVaccination;

  const isSizeMissing = !targetPet?.size?.size_label;
  const isCoatMissing = !targetPet?.breed?.breed_coat;

  const petPhoto =
    targetPet?.photo ||
    targetPet?.image ||
    targetPet?.profile_image ||
    targetPet?.pet_image ||
    "";

  return (
    <Box
      p={{ xs: 1.75, sm: 2 }}
      display="flex"
      alignItems="flex-start"
      gap={{ xs: 1.5, sm: 2 }}
      border="1px solid #e2e8f0"
      borderRadius={3}
      bgcolor="#ffffff"
      sx={{
        mb: 2,
        position: "relative",
        boxShadow:
          "0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 1px 2px -1px rgba(0, 0, 0, 0.04)",
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: "0 4px 14px -2px rgba(15, 23, 42, 0.08)",
          borderColor: "#cbd5e1",
        },
      }}
    >
      {/* Pet Avatar / Icon */}
      <div className="flex-shrink-0 pt-0.5">
        {petPhoto ? (
          <img
            src={petPhoto}
            alt={targetPet.name || "Pet"}
            className="w-12 h-12 rounded-2xl object-cover border border-slate-200/80 shadow-2xs"
          />
        ) : (
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-50 via-white to-blue-50/70 border border-indigo-100 flex items-center justify-center text-indigo-600 shadow-2xs">
            {targetPet?.pet_type?.name === "Cat" ||
            targetPet?.type === "Cat" ? (
              <PiCat className="w-7 h-7 text-indigo-600" />
            ) : (
              <PiDog className="w-7 h-7 text-indigo-600" />
            )}
          </div>
        )}
      </div>

      <Box flex={1} minWidth={0} pr={{ xs: 4, sm: 5 }}>
        {/* Row 1: Pet Name + Rabies Status Badge */}
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.25}>
          {targetPet?.name && (
            <Typography
              component="span"
              fontWeight="700"
              fontSize="1.05rem"
              color="#0f172a"
              sx={{ textTransform: "capitalize", letterSpacing: "-0.01em" }}
            >
              {targetPet.name}
            </Typography>
          )}

          {/* Rabies Vaccinated Badge */}
          {vaccinationEnabled && (
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border transition-colors ${
                showVaccinationLoading
                  ? "bg-amber-50 text-amber-700 border-amber-200"
                  : hasVaccination
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-rose-50 text-rose-700 border-rose-200"
              }`}
            >
              {vaccinationLoading ? (
                <span>Loading vaccination status...</span>
              ) : hasVaccination ? (
                <>
                  <FaCheck className="text-[10px] text-emerald-600" />
                  <span>Rabies Vaccinated</span>
                </>
              ) : (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                  <span>Rabies Vaccination is missing.</span>
                </>
              )}
            </span>
          )}
        </Box>

        {/* Row 2: Basic Info (Type • Breed • Size • Age • Gender • Weight • Color Circle Swatch) */}
        {(infoFields.length > 0 || colors.length > 0) && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs md:text-[13px] text-slate-600 mt-1 leading-normal">
            {infoFields.map((field, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && (
                  <span className="text-slate-300 font-light select-none">•</span>
                )}
                <span>{field}</span>
              </React.Fragment>
            ))}

            {colors.length > 0 && (
              <>
                {infoFields.length > 0 && (
                  <span className="text-slate-300 font-light select-none">•</span>
                )}
                <span className="inline-flex items-center gap-1.5 flex-wrap">
                  {colors.map((c, i) => {
                    const hex = getColorHex(c);
                    return (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1.5 text-slate-700 font-medium"
                      >
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full border border-black/15 shadow-2xs flex-shrink-0"
                          style={{ backgroundColor: hex }}
                          title={c}
                        />
                        <span>{c}</span>
                        {i < colors.length - 1 && (
                          <span className="text-slate-300">,</span>
                        )}
                      </span>
                    );
                  })}
                </span>
              </>
            )}
          </div>
        )}

        {/* Row 3: Diet Badges (if any) */}
        {(dietList.length > 0 || targetPet?.meals_per_day) && (
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            <span
              className="inline-flex items-center justify-center text-amber-500 mr-0.5"
              title="Diet"
              aria-label="Diet"
            >
              <FaUtensils className="text-xs" />
            </span>
            {dietList.map((d, i) => {
              const isVeg = /veg(?!.*non)/i.test(d) || /vegetarian/i.test(d);
              const isNonVeg = /non.*veg/i.test(d) || /meat|raw/i.test(d);
              return (
                <span
                  key={i}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                    isVeg
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : isNonVeg
                        ? "bg-rose-50 text-rose-700 border-rose-200"
                        : "bg-amber-50 text-amber-700 border-amber-200"
                  }`}
                  title={`Diet: ${d}`}
                >
                  {isVeg ? (
                    <span className="inline-flex items-center justify-center w-3 h-3 border border-emerald-600 rounded-xs bg-white p-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                    </span>
                  ) : isNonVeg ? (
                    <span className="inline-flex items-center justify-center w-3 h-3 border border-rose-600 rounded-xs bg-white p-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-600" />
                    </span>
                  ) : null}
                  <span>{d}</span>
                </span>
              );
            })}

            {targetPet?.meals_per_day && (
              <span className="px-2.5 py-0.5 text-xs text-slate-600 bg-slate-50 rounded-full border border-slate-200 font-medium">
                {targetPet.meals_per_day} meals/day
              </span>
            )}
          </div>
        )}

        {/* Row 4: Medical Conditions */}
        {medicalConditions.length > 0 && (
          <div className="flex items-center flex-wrap gap-1.5 mt-2">
            <span
              className="inline-flex items-center justify-center text-rose-500 mr-0.5"
              title="Medical Conditions"
              aria-label="Medical Conditions"
            >
              <FaHeartbeat className="text-xs" />
            </span>
            {medicalConditions.map((mc, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-50 text-rose-700 border border-rose-200/80"
              >
                {mc}
              </span>
            ))}
          </div>
        )}

        {/* Row 5: Vaccinations List */}
        {vaccineList.length > 0 && (
          <div className="flex items-center flex-wrap gap-1.5 mt-2">
            <span
              className="inline-flex items-center justify-center text-indigo-500 mr-0.5"
              title="Vaccines"
              aria-label="Vaccines"
            >
              <FaSyringe className="text-xs" />
            </span>
            {vaccineList.map((vax, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200/80"
              >
                <FaCheck className="text-[9px] text-emerald-600" />
                <span>{vax}</span>
              </span>
            ))}
          </div>
        )}

        {/* Row 6: Tags */}
        {tags.length > 0 && (
          <div className="flex items-center flex-wrap gap-1.5 mt-2">
            <span
              className="inline-flex items-center justify-center text-sky-500 mr-0.5"
              title="Tags"
              aria-label="Tags"
            >
              <FaTag className="text-xs" />
            </span>
            {tags.map((tag, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-sky-50 text-sky-700 border border-sky-200/80"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </Box>

      {/* Edit Button */}
      <IconButton
        onClick={() => onEdit()}
        size="small"
        sx={{
          position: "absolute",
          top: 10,
          right: 10,
          bgcolor: "#f8fafc",
          border: "1px solid #e2e8f0",
          color: "#64748b",
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
          transition: "all 0.15s ease",
          "&:hover": {
            bgcolor: "#eef2ff",
            borderColor: "#c7d2fe",
            color: "#4f46e5",
          },
        }}
        title="Edit Pet"
        aria-label="Edit Pet"
      >
        <EditIcon
          fontSize="small"
          sx={{ fontSize: "1.05rem" }}
        />
      </IconButton>

      {/* Missing Info Warnings */}
      {(isSizeMissing && isSizeRequired) ||
      (isCoatMissing && isCoatRequired) ? (
        <Box
          sx={{
            position: "absolute",
            top: 10,
            right: 48,
            display: "flex",
            gap: 0.5,
            flexWrap: "wrap",
          }}
        >
          {isSizeMissing && isSizeRequired && (
            <Box
              sx={{
                bgcolor: "#ef4444",
                color: "white",
                fontSize: "0.7rem",
                fontWeight: "bold",
                px: 1,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              Size required
            </Box>
          )}
          {isCoatMissing && isCoatRequired && (
            <Box
              sx={{
                bgcolor: "#ef4444",
                color: "white",
                fontSize: "0.7rem",
                fontWeight: "bold",
                px: 1,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              Coat required
            </Box>
          )}
        </Box>
      ) : null}
    </Box>
  );
};

export default PetCard;
